<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { usePlatform } from '@/composables/usePlatform'
import { Distribution } from '@/enums/Distribution'
import { LOGIN_MODAL } from '@/types/Modals'
import { useLocalizedRouter } from '@/composables/localizedRouter'
import DistributionToggle from '@/components/Buttons/DistributionToggle.vue'
import Button from '@/components/partials/Buttons/Button/Button.vue'
import AddressButton from '@/pages/establishment/components/redesign/EstablishmentToolbarTop/AddressButton.vue'
import MenuTooltip from '@/components/Menu/MenuTooltip.vue'
import Tooltip from '@/components/partials/Tooltip.vue'
import LanguageSwitcherCircle from '@/pages/home/components/Navigation/LanguageSwitcherCircle.vue'
import { useAuthStore } from '@/stores/useAuthStore'
import { useModalsStore } from '@/stores/useModalsStore'
import { useSessionStore } from '@/stores/useSessionStore'
import { Platform } from '@/enums/Platform'
import { templateRef } from "@vueuse/core"
import Icon from "@/components/partials/Icon.vue"
import { storeToRefs } from "pinia"

const emit = defineEmits([ 'openLocationModal' ])

const router = useRouter()
const authStore = useAuthStore()
const sessionStore = useSessionStore()
const platform = usePlatform()
const { t } = useI18n()
const { localizedRoute } = useLocalizedRouter()
const { toggle } = useModalsStore()

const route = useRoute()

const navbarTop = templateRef('navbarTop')
const isSticky = ref<boolean>(false)
const { isAuthenticated } = storeToRefs(authStore)

const handleScroll = () => {
  const { height } = navbarTop.value?.getBoundingClientRect() || {}

  if (typeof height === 'number') {
    isSticky.value = window.scrollY > height
  }
}

const openLoginModal = () => {
  toggle(LOGIN_MODAL, true)
}

const navigateToAccount = async () => {
  await router.push(localizedRoute({ name: 'customer-account' }))
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll)
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', handleScroll)
})

const distribution = computed({
  get: () => sessionStore.distribution,
  set: async val => sessionStore.setDistribution(val),
})

function openAddressModal() {
  emit('openLocationModal')
}

async function toggleDistributionType(type: Distribution) {
  const location = sessionStore.location

  if (type === Distribution.Delivery && location === null) {
    openAddressModal()

    return
  }

  distribution.value = type
}

const showBottomBar = computed<boolean>(() => !String(route.name).endsWith('slug'))
</script>

<template>
  <nav class="nav"
       :class="{ 'nav--sticky': isSticky }"
       data-test-id="nav-bar-mobile">
    <div ref="navbarTop"
         :class="[ 'nav__top', { 'nav__top--ios': platform == Platform.IOS, 'visual__image--android': platform == Platform.ANDROID } ]">
      <RouterLink :to="localizedRoute({ name: 'home' })"
                  class="nav__logo-link"
                  data-test-id="navbar-logo">
        <img class="nav__logo"
             alt="Bistroo"
             src="/assets/img/brand/bistroo-logo.svg"
             width="126"
             data-not-lazy>
      </RouterLink>

      <div class="nav__right">
        <Button
            type="text"
            data-test-id="user-account-button"
            @click="navigateToAccount"
            v-if="isAuthenticated"
        >
          <template #icon>
            <Icon name="user-3-line"/>
          </template>
        </Button>
        <Button data-test-id="user-account-button"
                type="text"
                @click="openLoginModal"
                v-else-if="!isAuthenticated">
          {{ t('buttons.log-in') }}
        </Button>

        <LanguageSwitcherCircle small></LanguageSwitcherCircle>

        <Tooltip class="nav__menu-tooltip">
          <template #default="{ active }">
            <Button
                type="text"
                data-test-id="mobile-menu"
                class="nav__menu-button">
              <template #icon>
                <Icon name="cross-line" v-if="active"/>
                <Icon name="menu-line" v-else/>
              </template>
            </Button>
          </template>
          <template #content>
            <MenuTooltip/>
          </template>
        </Tooltip>
      </div>
    </div>
    <div class="nav__bottom" v-if="showBottomBar">
      <AddressButton with-dropdown-arrow @open-modal="openAddressModal"/>
      <DistributionToggle v-model="distribution" variant="light" @toggle="toggleDistributionType"/>
    </div>
  </nav>
</template>

<style lang="scss" scoped>
@import "@/assets/css/mixins/breakpoints-up.scss";
@import "@/assets/css/mixins/styling.scss";

.nav {
  $self:            &;
  background-color: #ffffff;
  position:         relative;
  z-index:          10;
  font-weight:      700;

  @include lg-up {
    display: none;
  }

  &__top,
  &__bottom {
    display:     flex;
    justify-content: space-between;
    align-items: center;
  }

  &__top {
    padding: 0.75rem 0.4rem 0 1.4rem;

    &:deep(.button--icon-only .b-icon-icon) {
      width: 21px;
      height: 21px;
    }

    &:deep(.b-icon-icon svg) {
      width: 24px;
      height: 24px;
    }
  }

  &__bottom {
    padding: 0.5rem 1rem 0.5rem 0.25rem;

    &:deep(.address-button__text-zipcode) {
      font-weight: 700;
    }

    &:deep(.address-button__content .b-icon-icon svg) {
      width: 21px;
      height: 21px;
    }

    &:deep(.address-button__content) {
      border-color: transparent;
    }
  }

  &__top,
  &__bottom {
    &--ios {
      padding-top: env(safe-area-inset-top);
    }

    &--android {
      padding-top: calc(env(safe-area-inset-top) + 0.75rem) !important;
    }
  }

  &__logo {
    width: 6.5rem;
    height: auto;
  }

  &__right {
    display: flex;
    align-items: center;
    height:  100%;
  }

  &__menu-tooltip {
    &:deep(.tooltip__dropdown) {
      width:      calc(100% - 2rem);
      height:     max-content;
      border-radius: 2rem;
      right:      1rem;
      box-shadow: 0 1rem 4rem rgba(0, 0, 0, 0.2);
    }

    &:deep(.tooltip__inner),
    &:deep(.menu-tooltip) {
      height: max-content;
    }
  }

  &--sticky {
    position:   sticky;
    top:        0;
    padding-top: env(safe-area-inset-top, 0);
    box-shadow: 0 0 1rem rgba(0, 0, 0, 0.15);

    #{$self}__top {
      display: none;
    }

    #{$self}__bottom {
      padding: 1rem 1rem 1rem 0.25rem;
    }
  }
}
</style>
